/**
 * Arquitetura de Onboarding — a "carta da diretora" e o roteiro que o
 * cliente recebe por um link pessoal (não editável por ele). Fonte: pedido
 * de 26/jul. O conteúdo padrão abaixo é o texto da própria Lara, na íntegra
 * — nada condensado. Ela pode editar cada campo depois, pela tela de
 * Onboarding no painel; isto aqui é só o ponto de partida.
 */

export interface OnboardingPillar {
  title: string;
  body: string;
}

export interface OnboardingStep {
  number: string; // "01".."08"
  title: string;
  body: string;
}

export interface OnboardingRuleItem {
  label: string;
  body: string;
}

export interface OnboardingGlossaryTerm {
  term: string;
  definition: string;
}

export interface OnboardingContent {
  carta: {
    title: string;
    paragraphs: string[];
    signature: string;
  };
  jeitoUnik: {
    title: string;
    intro: string;
    pilares: OnboardingPillar[];
  };
  roteiro: {
    title: string;
    intro: string;
    passos: OnboardingStep[];
  };
  regras: {
    title: string;
    intro: string;
    itens: OnboardingRuleItem[];
    precisoDeVoceTitle: string;
    precisoDeVoceIntro: string;
    precisoDeVoceItens: OnboardingRuleItem[];
  };
  dicionario: {
    title: string;
    intro: string;
    termos: OnboardingGlossaryTerm[];
  };
  encerramento: {
    title: string;
    paragraphs: string[];
    signature: string;
    handles: string[];
  };
}

export const DEFAULT_ONBOARDING_CONTENT: OnboardingContent = {
  carta: {
    title: "Carta da diretora",
    paragraphs: [
      "Quando eu era criança, a Barbie mais bem-morada do bairro era a minha. Não porque alguém comprou a casa dela, mas porque eu construí. Caixa de sapato virava sala. Retalho que iam jogar no lixo virava cortina. Sobra de papel virava um look que loja nenhuma tinha. Enquanto as outras crianças brincavam com o que já vinha pronto, eu simplesmente não conseguia. Eu precisava criar.",
      "Demorei pra entender que aquilo tinha nome. Não era \"menininha inventando moda\", era o jeito que o meu cérebro funciona até hoje. Eu nunca, em momento nenhum, me contentei com o básico. Com o simples. Com o \"tá bom assim\". Quando todo mundo ia por um caminho, eu já estava enxergando o caminho que ninguém tinha visto.",
      "Essa menina nunca foi embora.",
      "Eu levei ela comigo pro trabalho e foram seis anos posicionando marcas, ouvindo sempre a mesma pergunta dos meus clientes: \"como é que você pensou nisso?\". A resposta demorou, mas veio: o meu jeito de pensar é ÚNICO. E um dia eu entendi que estava na hora de fazer pela minha marca aquilo que eu vinha fazendo pela dos outros.",
      "Só que tinha uma coisa me incomodando. Eu olhava pro mercado de posicionamento e via gente copiando gente. Modelo que se repete, post que se replica, fórmula aplicada sem ninguém nunca sentar pra estudar de verdade o negócio de quem está pagando. Um mercado inteiro fazendo igual e chamando aquilo de estratégia.",
      "A UniK nasceu desse incômodo. Nasceu pra provar que existe outro jeito — um jeito UNIKO — de fazer aquilo que todo mundo acha que já sabe fazer.",
      "Aqui não existe \"um post e acabou\". Aqui eu entro na sua marca. Eu estudo o seu negócio, o seu sonho e o seu objetivo como se fossem meus, porque, no instante em que você me contrata, eles passam a ser. Eu pego o seu jeito, a sua forma de pensar, aquilo que só existe em você, e transformo em algo que ninguém vai conseguir copiar.",
      "É por isso que eu escrevo essa carta. Pra te dizer, de pertinho, que você escolheu certo. Você não caiu em mãos comuns. Você está com quem vai cuidar da sua marca com a mesma obsessão criativa daquela menina montando cenário de caixa de sapato, só que agora com seis anos de estratégia por trás de cada decisão.",
      "E aquela criatividade que vive em mim desde sempre? A partir de agora, ela vai viver em você também.",
      "Boas-vindas à UniK. Vamos criar algo que ninguém nunca viu.",
    ],
    signature: "tia Lara",
  },
  jeitoUnik: {
    title: "O jeito Unik",
    intro:
      "Três coisas guiam tudo o que a gente faz por aqui. Não são palavras bonitas numa parede, são o motivo de cada escolha dentro do seu perfil.",
    pilares: [
      {
        title: "Criatividade",
        body:
          "Eu não sei fazer igual. E quando o assunto já foi feito por todo mundo, eu faço de um jeito que ninguém nunca pensou. Criatividade, pra mim, é pegar o seu trabalho e enxergar a saída que ninguém viu. Aquela que atrai exatamente quem você quer atrair e te leva exatamente aonde você quer chegar. Acomodação não mora aqui.",
      },
      {
        title: "Autenticidade",
        body:
          "Esquece o posicionamento chato, engessado, de guru que diz que você precisa ser perfeito o tempo todo. Aqui, posicionar é leve. É você sendo você, sem medo de julgamento. O meu trabalho é enxergar quem você verdadeiramente é e mostrar isso pro mundo. Porque a autenticidade só é autêntica quando é real.",
      },
      {
        title: "Força",
        body:
          "Quando você me contrata, eu visto a sua marca. Você vai me ver estudando, escrevendo, batalhando, mergulhando no que você entrega, preocupada de verdade com o seu objetivo. Eu me dedico mil por cento porque o seu sonho passa a ser parte do meu. E eu carrego o sonho de quem confia em mim como se fosse meu.",
      },
    ],
  },
  roteiro: {
    title: "O roteiro",
    intro: "Toda grande produção segue um roteiro. O seu começa aqui e cada cena tem um propósito.",
    passos: [
      {
        number: "01",
        title: "O convite",
        body: "Você me procura querendo entender o meu trabalho. Eu te conto como eu penso, o que me diferencia e por que aqui nunca vai ser \"só mais um perfil bonitinho\".",
      },
      {
        number: "02",
        title: "A leitura de cena",
        body: "Nossa reunião de alinhamento. A gente conversa, eu abro o seu perfil, entro na sua marca, entendo o seu sonho e o seu objetivo e te explico, do começo ao fim, como tudo vai funcionar.",
      },
      {
        number: "03",
        title: "O aceite",
        body: "Você decide. Eu te envio o contrato com tudo certinho, todas as cláusulas claras, e a gente oficializa essa parceria.",
      },
      {
        number: "04",
        title: "O briefing",
        body: "Você preenche o briefing da UniK: perguntas pensadas pra revelar você. Quanto mais verdade você colocar aqui, mais a sua cara o resultado vai ter.",
      },
      {
        number: "05",
        title: "A investigação",
        body: "Agora eu viro detetive. Estudo o seu briefing de cabo a rabo, leio as entrelinhas, percebo até o que você não disse com todas as letras. É assim que eu sinto a sua marca por dentro e só então eu consigo fazer um trabalho extraordinário.",
      },
      {
        number: "06",
        title: "O mapa de posicionamento",
        body: "A entrega-mãe. Aqui eu monto o seu norte: objetivo do perfil, persona, público-alvo, arquétipo, comunicação, branding e metas de curto, médio e longo prazo. Entra também a análise completa dos seus concorrentes… pontos fortes, pontos fracos, o que vale aproveitar e o que a gente nunca vai repetir. É uma pesquisa de mercado feita não só sobre o seu negócio, mas sobre a sua vida.",
      },
      {
        number: "07",
        title: "O cronograma editorial",
        body: "Junto com o mapa, você recebe o plano em ação: duas publicações por semana; carrossel ou vídeo, com dias, horários, temas e o porquê de cada escolha. Tudo conectado às linhas editoriais aprovadas e aos seus objetivos.",
      },
      {
        number: "08",
        title: "Ação!",
        body: "Cronograma aprovado, a criação começa. Carrossel, eu entrego pronto. Vídeo, eu te mando o roteiro, você grava, me envia, e eu edito e entrego. É aqui que a sua marca começa a virar única na prática.",
      },
    ],
  },
  regras: {
    title: "As regras do set",
    intro: "Todo set de gravação tem regras e são elas que fazem a produção fluir sem ruído. Aqui estão as nossas.",
    itens: [
      { label: "Revisões", body: "Até 2 ajustes por entrega, seja post ou vídeo. Tempo de sobra pra deixar tudo do seu jeito." },
      { label: "Entregas", body: "Duas publicações por semana. Eu te envio de 1 a 2 dias antes da data de publicação, pra dar tempo de revisar e aprovar com calma. Depois, eu mesma programo no dia e horário certos." },
      { label: "Onde a gente conversa", body: "No grupo de WhatsApp da UniK. É o nosso set oficial." },
      { label: "Tempo de resposta", body: "Respondo em até 24h, quase sempre bem antes. Se for urgente, é só me ligar." },
    ],
    precisoDeVoceTitle: "O que eu preciso de você",
    precisoDeVoceIntro:
      "Pra mágica acontecer, eu não trabalho sozinha. A marca é sua; a estratégia é minha; o resultado é nosso. E isso só funciona com:",
    precisoDeVoceItens: [
      { label: "Companheirismo e proatividade", body: "A gente está reconstruindo uma marca juntos. Preciso de você presente, comunicativo e preocupado com o seu perfil tanto quanto eu sou." },
      { label: "Fotos e materiais", body: "Sempre vou pedir o que a estratégia exigir, de forma personalizada pra cada objetivo." },
      { label: "Acessos", body: "Login e senha do Instagram, pra eu cuidar de tudo de pertinho." },
      { label: "Aprovações no prazo", body: "Pra nada travar no caminho." },
      { label: "Comunicação e clareza", body: "Quanto mais a gente se fala, melhor eu te enxergo e melhor eu te traduzo." },
    ],
  },
  dicionario: {
    title: "Dicionário Unik",
    intro:
      "Pra gente falar a mesma língua durante toda a produção, aqui vão os termos que você vai me ver usando explicados sem juridiquês e sem chatice.",
    termos: [
      { term: "Posicionamento", definition: "É o lugar que a sua marca ocupa na cabeça das pessoas. Não é o que você diz que é, é o que elas sentem quando pensam em você. O meu trabalho é fazer esse lugar ser único." },
      { term: "Persona", definition: "É o retrato falado do seu cliente ideal: dores, desejos, o que assiste, o que tira o sono dele. Quanto mais nítido esse retrato, mais certeiro fica cada conteúdo." },
      { term: "Público-alvo", definition: "É o grupo que a gente quer alcançar, recortado por idade, interesse e comportamento. A persona é uma pessoa; o público-alvo é a plateia inteira." },
      { term: "Linhas editoriais", definition: "São os grandes temas que a sua marca vai falar. Os pilares de assunto que se repetem, constroem autoridade e nunca viram repetição." },
      { term: "Arquétipo", definition: "É a personalidade da sua marca traduzida num modelo universal… a Heroína, a Sábia, a Rebelde. Ele dá coerência a tudo, do jeito de escrever à escolha das cores." },
      { term: "Tom de voz", definition: "É o jeito da sua marca falar. Se ela fosse uma pessoa numa conversa, como soaria? Esse tom é o que faz cada post parecer você, do primeiro ao último." },
      { term: "Branding", definition: "É tudo que torna a sua marca reconhecível e inesquecível; identidade visual, linguagem, sensação. A soma das peças que fazem alguém dizer \"isso é a sua cara\"." },
      { term: "Mapa de posicionamento", definition: "O documento-mãe da nossa estratégia. Onde a gente está, aonde quer chegar e o caminho criativo pra isso. O seu norte e o meu." },
      { term: "Cronograma editorial", definition: "É o calendário do que vai ao ar: o quê, quando e por quê. Organiza a criação e garante constância; o combustível de todo perfil que cresce." },
      { term: "Análise de concorrentes", definition: "É o estudo de quem disputa a mesma atenção que você. O que funciona pra eles, o que não funciona, e o espaço que está sobrando pra gente ocupar." },
    ],
  },
  encerramento: {
    title: "Ação!",
    paragraphs: [
      "Agora é com a gente. A claquete está na mão, a cena está montada, e a sua marca está prestes a virar algo que ninguém nunca viu.",
      "Aqui nada se copia. Tudo se cria.",
      "Bora?",
    ],
    signature: "tia Lara · UNIK",
    handles: ["@falaunik", "@laraballd"],
  },
};
